/**
 * =============================================================================
 * setera-web
 * =============================================================================
 * Copyright (c) 2025 HG Digital/Setera
 * Todos os direitos reservados.
 * 
 * Este software é confidencial e de propriedade da HG Digital/Setera.
 * Seu uso está sujeito aos termos do contrato de licença.
 * -----
 * Criado em : 26-06-2025
 * Autor     : diegobrock
 * Projeto   : setera-web
 * Versão    : 1.0.0
 * =============================================================================
 */

import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export interface SRSStreamInfo {
  simCard: string;
  streamName: string;
  protocol: 'hls' | 'flv' | 'webrtc';
  url: string;
  isActive: boolean;
  viewerCount?: number;
}

export interface SRSConnectionStatus {
  connected: boolean;
  protocol: string;
  streamName: string;
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SRSStreamingService {
  private connectionStatusSubject = new BehaviorSubject<SRSConnectionStatus>({
    connected: false,
    protocol: '',
    streamName: ''
  });

  private streamInfoSubject = new BehaviorSubject<SRSStreamInfo | null>(null);
  private peerConnection: RTCPeerConnection | null = null;
  private localStream: MediaStream | null = null;

  public connectionStatus$ = this.connectionStatusSubject.asObservable();
  public streamInfo$ = this.streamInfoSubject.asObservable();

  private srsConfig = environment.apisConfig.seteraStreaming.srs;

  /**
   * Gera URL de stream FLV para um dado UUID de sessão
   */
  public generateFLVStreamUrlWithSession(sessionUuid: string, simCard: string): SRSStreamInfo {
    return {
      simCard,
      streamName: sessionUuid,
      protocol: 'flv',
      url: `${this.srsConfig.flvUrl}/${sessionUuid}.flv`,
      isActive: false
    };
  }

  /**
   * @deprecated Não usar mais. Sempre use métodos baseados em sessão.
   */
  public generateStreamUrls(simCard: string): SRSStreamInfo[] {
    throw new Error('generateStreamUrls(simCard) está obsoleto. Use generateFLVStreamUrlWithSession(sessionUuid, simCard)');
  }

  /**
   * Verifica se um stream FLV está ativo no SRS usando UUID da sessão
   */
  public async checkStreamStatusWithSession(sessionUuid: string): Promise<boolean> {
    try {
      const streamName = sessionUuid;
      // Usa HEAD para não baixar o arquivo inteiro
      const response = await fetch(`${this.srsConfig.flvUrl}/${streamName}.flv`, { method: 'HEAD' });
      return response.ok;
    } catch (error) {
      console.error('Erro ao verificar status do stream FLV:', error);
      return false;
    }
  }

  /**
   * Verifica se um stream está ativo usando URL específica (para URLs da API)
   */
  public async checkStreamStatusWithUrl(streamUrl: string): Promise<boolean> {
    try {
      // Usa HEAD para não baixar o arquivo inteiro
      const response = await fetch(streamUrl, { method: 'HEAD' });
      return response.ok;
    } catch (error) {
      console.error('Erro ao verificar status do stream com URL:', streamUrl, error);
      return false;
    }
  }

  /**
   * Inicia streaming via HTTP-FLV usando UUID da sessão
   */
  public startFLVStreamWithSession(sessionUuid: string, simCard: string): SRSStreamInfo {
    const streamInfo = this.generateFLVStreamUrlWithSession(sessionUuid, simCard);
    streamInfo.isActive = true;
    this.streamInfoSubject.next(streamInfo);
    this.connectionStatusSubject.next({
      connected: true,
      protocol: 'flv',
      streamName: streamInfo.streamName
    });
    return streamInfo;
  }

  /**
   * Inicia streaming via WebRTC usando UUID da sessão
   *
   * OBS: No cenário atual, apenas FLV é suportado. Este método está mantido apenas para referência futura.
   */
  public async startWebRTCStreamWithSession(sessionUuid: string, simCard: string): Promise<SRSStreamInfo> {
    // Apenas para referência futura. Não utilizar em produção enquanto só FLV for suportado.
    throw new Error('WebRTC não suportado neste ambiente. Apenas FLV está habilitado.');
  }

  /**
   * Para o streaming atual
   */
  public stopStream(): void {
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }

    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
      this.localStream = null;
    }

    this.streamInfoSubject.next(null);
    this.connectionStatusSubject.next({
      connected: false,
      protocol: '',
      streamName: ''
    });
  }

  /**
   * Retorna o stream local (para WebRTC)
   */
  public getLocalStream(): MediaStream | null {
    return this.localStream;
  }

  /**
   * Obtém estatísticas do SRS
   */
  public async getSRSStats(): Promise<any> {
    try {
      const response = await fetch(`http://${this.srsConfig.host}:${this.srsConfig.port}/api/v1/streams/`);
      return await response.json();
    } catch (error) {
      console.error('Erro ao obter estatísticas do SRS:', error);
      return null;
    }
  }

  /**
   * Valida se uma URL de stream é acessível
   */
  public async validateStreamUrl(streamUrl: string): Promise<boolean> {
    try {
      const response = await fetch(streamUrl, { method: 'HEAD' });
      return response.ok;
    } catch (error) {
      console.error('Erro ao validar URL do stream:', error);
      return false;
    }
  }
} 